import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowUpRight,
  BadgeCheck,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  MapPin,
  PackageCheck,
  ShoppingBag,
} from 'lucide-react';
import { lazy, Suspense, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { StorePicker } from '../../components/StorePicker';
import { apiRequest } from '../../lib/api';
import type {
  DashboardAttendanceItem,
  DashboardDailyBAPresenceDetail,
  DashboardOverview,
  DashboardProductDetail,
  DashboardSalesItem,
  DashboardStoreDetail,
  Membership,
} from '../../types/domain';
import { DailyBAPresenceModal } from './DailyBAPresenceModal';
import { ProductSalesDetailModal } from './ProductSalesDetailModal';
import { StoreSalesDetailModal } from './StoreSalesDetailModal';

const DashboardCharts = lazy(async () => {
  const module = await import('./DashboardCharts');
  return { default: module.DashboardCharts };
});

interface DashboardFilters {
  client: string;
  brand: string;
  agency: string;
  campaign: string;
  venue: string;
  dateRange: DateRangePreset;
  dateFrom: string;
  dateTo: string;
}

type DateRangePreset = 'today' | '7_days' | '30_days' | '90_days' | '180_days' | '365_days' | 'custom';

const dateRangeDays: Record<Exclude<DateRangePreset, 'custom'>, number> = {
  today: 0,
  '7_days': 7,
  '30_days': 30,
  '90_days': 90,
  '180_days': 180,
  '365_days': 365,
};

const todayFormatter = new Intl.DateTimeFormat('en-PK', {
  dateStyle: 'full',
  timeZone: 'Asia/Karachi',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'Asia/Karachi',
});

const currencyFormatter = new Intl.NumberFormat('en-PK', {
  style: 'currency',
  currency: 'PKR',
  maximumFractionDigits: 2,
});

function pakistanDateInput(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Karachi',
  }).formatToParts(date);
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

function dateInputDaysBefore(value: string, days: number) {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function initialFilters(
  searchParams?: URLSearchParams,
  memberships: Membership[] = [],
): DashboardFilters {
  const today = pakistanDateInput();
  const organisationMemberships = [...new Map(
    memberships.map((membership) => [membership.organizationId, membership]),
  ).values()];
  const soleOrganisation = organisationMemberships.length === 1
    ? organisationMemberships[0]
    : undefined;
  const clientFromUrl = searchParams?.get('client') ?? '';

  return {
    client: clientFromUrl || (
      soleOrganisation?.organizationType === 'client' ? soleOrganisation.organizationId : ''
    ),
    brand: searchParams?.get('brand') ?? '',
    agency: soleOrganisation?.organizationType === 'agency' ? soleOrganisation.organizationId : '',
    campaign: '',
    venue: '',
    dateRange: '30_days',
    dateFrom: dateInputDaysBefore(today, 30),
    dateTo: today,
  };
}

function readableReason(value: string) {
  return value ? value.replaceAll('_', ' ') : 'Validation exception';
}

export function DashboardPage() {
  const { accessToken, memberships, selectedWorkspace } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceId = selectedWorkspace?.id ?? null;
  const [filterState, setFilterState] = useState<{
    workspaceId: string | null;
    filters: DashboardFilters;
  }>(() => ({ workspaceId, filters: initialFilters(searchParams, memberships) }));
  const [selectedStore, setSelectedStore] = useState<{
    workspaceId: string | null;
    venueId: string;
  } | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<{
    productId: string;
    workspaceId: string | null;
  } | null>(null);
  const [selectedDailyBAPresence, setSelectedDailyBAPresence] = useState<{
    date: string;
    workspaceId: string | null;
  } | null>(null);
  const filters = filterState.workspaceId === workspaceId
    ? filterState.filters
    : initialFilters(undefined, memberships);
  const selectedStoreId = selectedStore?.workspaceId === workspaceId
    ? selectedStore.venueId
    : null;
  const selectedProductId = selectedProduct?.workspaceId === workspaceId
    ? selectedProduct.productId
    : null;
  const selectedDailyBAPresenceDate = selectedDailyBAPresence?.workspaceId === workspaceId
    ? selectedDailyBAPresence.date
    : null;
  const overviewPath = (() => {
    const params = new URLSearchParams({
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
    });
    if (selectedWorkspace?.organizationId) {
      params.set('workspace', selectedWorkspace.organizationId);
    }
    if (filters.client) params.set('client', filters.client);
    if (filters.brand) params.set('brand', filters.brand);
    if (filters.agency) params.set('agency', filters.agency);
    if (filters.campaign) params.set('campaign', filters.campaign);
    if (filters.venue) params.set('venue', filters.venue);
    return `/overview/?${params.toString()}`;
  })();
  const overview = useQuery({
    queryKey: ['overview', overviewPath, accessToken],
    enabled: Boolean(accessToken),
    placeholderData: (previousData) => previousData,
    queryFn: () => apiRequest<DashboardOverview>(
      overviewPath,
      accessToken ? { accessToken } : {},
    ),
  });
  const data = overview.data;
  const summary = data?.summary;
  const storeDetailPath = (() => {
    if (!selectedStoreId) return null;
    const params = new URLSearchParams({
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
    });
    if (selectedWorkspace?.organizationId) {
      params.set('workspace', selectedWorkspace.organizationId);
    }
    if (filters.client) params.set('client', filters.client);
    if (filters.brand) params.set('brand', filters.brand);
    if (filters.agency) params.set('agency', filters.agency);
    if (filters.campaign) params.set('campaign', filters.campaign);
    return `/overview/stores/${selectedStoreId}/?${params.toString()}`;
  })();
  const storeDetail = useQuery({
    queryKey: ['overview-store-detail', storeDetailPath, accessToken],
    enabled: Boolean(accessToken && storeDetailPath),
    queryFn: () => apiRequest<DashboardStoreDetail>(
      storeDetailPath!,
      accessToken ? { accessToken } : {},
    ),
  });
  const productDetailPath = (() => {
    if (!selectedProductId) return null;
    const params = new URLSearchParams({
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
    });
    if (selectedWorkspace?.organizationId) {
      params.set('workspace', selectedWorkspace.organizationId);
    }
    if (filters.client) params.set('client', filters.client);
    if (filters.brand) params.set('brand', filters.brand);
    if (filters.agency) params.set('agency', filters.agency);
    if (filters.campaign) params.set('campaign', filters.campaign);
    if (filters.venue) params.set('venue', filters.venue);
    return `/overview/products/${selectedProductId}/?${params.toString()}`;
  })();
  const productDetail = useQuery({
    queryKey: ['overview-product-detail', productDetailPath, accessToken],
    enabled: Boolean(accessToken && productDetailPath),
    queryFn: () => apiRequest<DashboardProductDetail>(
      productDetailPath!,
      accessToken ? { accessToken } : {},
    ),
  });
  const dailyBAPresenceDetailPath = (() => {
    if (!selectedDailyBAPresenceDate) return null;
    const params = new URLSearchParams({ date: selectedDailyBAPresenceDate });
    if (selectedWorkspace?.organizationId) {
      params.set('workspace', selectedWorkspace.organizationId);
    }
    if (filters.client) params.set('client', filters.client);
    if (filters.brand) params.set('brand', filters.brand);
    if (filters.agency) params.set('agency', filters.agency);
    if (filters.campaign) params.set('campaign', filters.campaign);
    if (filters.venue) params.set('venue', filters.venue);
    return `/overview/daily-ba-presence/?${params.toString()}`;
  })();
  const dailyBAPresenceDetail = useQuery({
    queryKey: ['overview-daily-ba-presence', dailyBAPresenceDetailPath, accessToken],
    enabled: Boolean(accessToken && dailyBAPresenceDetailPath),
    queryFn: () => apiRequest<DashboardDailyBAPresenceDetail>(
      dailyBAPresenceDetailPath!,
      accessToken ? { accessToken } : {},
    ),
  });

  function updateFilter<Key extends keyof DashboardFilters>(
    key: Key,
    value: DashboardFilters[Key],
  ) {
    setSelectedStore(null);
    setSelectedProduct(null);
    setSelectedDailyBAPresence(null);
    const nextFilters = {
      ...filters,
      [key]: value,
      ...(key === 'client' ? { brand: '' } : {}),
    };
    setFilterState({
      workspaceId,
      filters: nextFilters,
    });
    if (key === 'client' || key === 'brand') {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (nextFilters.client) next.set('client', nextFilters.client);
        else next.delete('client');
        if (nextFilters.brand) next.set('brand', nextFilters.brand);
        else next.delete('brand');
        return next;
      }, { replace: true });
    }
  }

  function resetFilters() {
    setSelectedStore(null);
    setSelectedProduct(null);
    setSelectedDailyBAPresence(null);
    setFilterState({ workspaceId, filters: initialFilters(undefined, memberships) });
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('client');
      next.delete('brand');
      return next;
    }, { replace: true });
  }

  function updateDateRange(dateRange: DateRangePreset) {
    setSelectedStore(null);
    setSelectedProduct(null);
    setSelectedDailyBAPresence(null);
    const today = pakistanDateInput();
    const dateTo = dateRange === 'today' ? today : filters.dateTo || today;
    setFilterState({
      workspaceId,
      filters: {
        ...filters,
        dateRange,
        dateTo,
        dateFrom: dateRange === 'custom'
          ? filters.dateFrom
          : dateInputDaysBefore(dateTo, dateRangeDays[dateRange]),
      },
    });
  }

  function updateCustomDate(key: 'dateFrom' | 'dateTo', value: string) {
    setSelectedStore(null);
    setSelectedProduct(null);
    setSelectedDailyBAPresence(null);
    setFilterState({
      workspaceId,
      filters: { ...filters, dateRange: 'custom', [key]: value },
    });
  }

  function recordsPath(
    resource: 'attendance' | 'sales',
    extra: Record<string, string> = {},
  ) {
    const params = new URLSearchParams({
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
      ...extra,
    });
    if (filters.campaign) params.set('campaign', filters.campaign);
    if (filters.venue) params.set('venue', filters.venue);
    if (filters.client) params.set('client', filters.client);
    if (filters.agency) params.set('agency', filters.agency);
    return `/${resource}?${params.toString()}`;
  }

  function updateDailyBAPresenceDate(date: string) {
    if (date < filters.dateFrom || date > filters.dateTo) return;
    setSelectedDailyBAPresence({ date, workspaceId });
  }

  function campaignPath() {
    const params = new URLSearchParams({
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
    });
    if (filters.client) params.set('client', filters.client);
    if (filters.agency) params.set('operator', filters.agency);
    return `/campaigns?${params.toString()}`;
  }

  function attendanceItemPath(item: DashboardAttendanceItem) {
    const day = item.captured_at.slice(0, 10);
    const site: Record<string, string> = {};
    if (item.venue_id) site.venue = item.venue_id;
    else if (item.activity_site_id) site.activity_site = item.activity_site_id;
    return recordsPath('attendance', {
      campaign: item.campaign_id,
      ...site,
      date_from: day,
      date_to: day,
      review_status: 'pending',
    });
  }

  function salesItemPath(item: DashboardSalesItem) {
    const day = item.sold_at.slice(0, 10);
    const site: Record<string, string> = {};
    if (item.venue_id) site.venue = item.venue_id;
    else if (item.activity_site_id) site.activity_site = item.activity_site_id;
    return recordsPath('sales', {
      campaign: item.campaign_id,
      ...site,
      date_from: day,
      date_to: day,
      review_status: 'pending',
    });
  }

  const metrics = summary ? [
    {
      label: 'Campaigns',
      value: summary.campaigns_total,
      helper: `${summary.campaigns_active} active in this period`,
      icon: BriefcaseBusiness,
      to: campaignPath(),
    },
    {
      label: 'Active assignments',
      value: summary.active_assignments,
      helper: 'Accepted work overlapping this period',
      icon: ClipboardList,
      to: '/assignments',
    },
    {
      label: 'Attendance events',
      value: summary.attendance_events,
      helper: 'Recorded check-ins and checkouts',
      icon: ClipboardCheck,
      to: recordsPath('attendance'),
    },
    {
      label: 'Attendance review',
      value: summary.attendance_pending,
      helper: 'Flagged events needing a decision',
      icon: AlertTriangle,
      to: recordsPath('attendance', { review_status: 'pending' }),
      tone: 'warning',
    },
    {
      label: 'Accepted sales',
      value: summary.sales_accepted_units,
      helper: `${currencyFormatter.format(Number(summary.sales_accepted_value))} accepted value`,
      icon: PackageCheck,
      to: recordsPath('sales'),
    },
    {
      label: 'Sales review',
      value: summary.sales_pending,
      helper: `${summary.sales_reports} reports in this period`,
      icon: ShoppingBag,
      to: recordsPath('sales', { review_status: 'pending' }),
      tone: 'warning',
    },
  ] : [];

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{todayFormatter.format(new Date())}</p>
          <h1>{selectedWorkspace?.name ?? 'Field overview'}</h1>
          <p>Attendance, sales, campaigns, and assignments from one authorised view.</p>
        </div>
        <span className="record-count"><CalendarDays size={16} /> {filters.dateFrom === filters.dateTo ? filters.dateFrom : `${filters.dateFrom} – ${filters.dateTo}`}</span>
      </header>

      <section className="content-card attendance-filter-card dashboard-filter-card">
        <div className="card-heading">
          <div><h2>Filter operations</h2><p>Every total and review queue uses the same workspace and reporting period.</p></div>
          <button className="text-button" onClick={resetFilters} type="button">Reset filters</button>
        </div>
        <div className="attendance-filter-grid dashboard-filter-grid">
          <label>
            Client
            <select aria-label="Dashboard client" onChange={(event) => updateFilter('client', event.target.value)} value={filters.client}>
              <option value="">All clients</option>
              {(data?.filters.clients ?? []).map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
            </select>
          </label>
          <label>
            Brand
            <select aria-label="Dashboard brand" onChange={(event) => updateFilter('brand', event.target.value)} value={filters.brand}>
              <option value="">All brands</option>
              {(data?.filters.brands ?? []).map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
            </select>
          </label>
          <label>
            Agency
            <select aria-label="Dashboard agency" onChange={(event) => updateFilter('agency', event.target.value)} value={filters.agency}>
              <option value="">All agencies</option>
              {(data?.filters.agencies ?? []).map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
            </select>
          </label>
          <label>
            Campaign
            <select aria-label="Dashboard campaign" onChange={(event) => updateFilter('campaign', event.target.value)} value={filters.campaign}>
              <option value="">All campaigns</option>
              {(data?.filters.campaigns ?? []).map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
            </select>
          </label>
          <label>
            Store
            <StorePicker
              allOptionLabel="All stores"
              ariaLabel="Dashboard store"
              onChange={(value) => updateFilter('venue', value)}
              options={(data?.filters.venues ?? []).map((option) => ({ id: option.id, name: option.name }))}
              value={filters.venue}
            />
          </label>
          <label>
            Date range
            <select aria-label="Dashboard date range" onChange={(event) => updateDateRange(event.target.value as DateRangePreset)} value={filters.dateRange}>
              <option value="today">Today</option>
              <option value="7_days">1 week</option>
              <option value="30_days">1 month</option>
              <option value="90_days">3 months</option>
              <option value="180_days">6 months</option>
              <option value="365_days">1 year</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          <label>Date from<input max={filters.dateTo} onChange={(event) => updateCustomDate('dateFrom', event.target.value)} type="date" value={filters.dateFrom} /></label>
          <label>Date to<input min={filters.dateFrom} onChange={(event) => updateCustomDate('dateTo', event.target.value)} type="date" value={filters.dateTo} /></label>
        </div>
      </section>

      <DataPanel loading={overview.isLoading} error={overview.isError} empty={false}>
        <>
          <section className="metric-grid dashboard-metrics" aria-label="Operational summary">
            {metrics.map(({ label, value, helper, icon: Icon, to, tone }) => (
              <Link className="metric-card dashboard-metric-card" key={label} to={to}>
                <span className={`icon-chip${tone === 'warning' ? ' attendance-warning' : ''}`}><Icon size={19} /></span>
                <p>{label}</p>
                <strong>{value}</strong>
                <small>{helper}</small>
                <span className="dashboard-metric-link">View records <ArrowUpRight size={13} /></span>
              </Link>
            ))}
          </section>

          {data?.charts ? (
            <Suspense fallback={<section className="content-card dashboard-charts-loading"><span className="button-spinner" /> Loading performance charts…</section>}>
              <DashboardCharts
                charts={data.charts}
                onDailyBAPresenceSelect={(date) => {
                  setSelectedProduct(null);
                  setSelectedStore(null);
                  updateDailyBAPresenceDate(date);
                }}
                onProductSelect={(productId) => {
                  setSelectedDailyBAPresence(null);
                  setSelectedStore(null);
                  setSelectedProduct({ productId, workspaceId });
                }}
                onStoreSelect={(venueId) => {
                  setSelectedDailyBAPresence(null);
                  setSelectedProduct(null);
                  setSelectedStore({ workspaceId, venueId });
                }}
              />
            </Suspense>
          ) : null}

          {selectedStoreId ? (
            <StoreSalesDetailModal
              detail={storeDetail.data}
              error={storeDetail.error}
              isLoading={storeDetail.isLoading}
              onClose={() => setSelectedStore(null)}
            />
          ) : null}

          {selectedProductId ? (
            <ProductSalesDetailModal
              detail={productDetail.data}
              error={productDetail.error}
              isLoading={productDetail.isLoading}
              onClose={() => setSelectedProduct(null)}
            />
          ) : null}

          {selectedDailyBAPresenceDate ? (
            <DailyBAPresenceModal
              detail={dailyBAPresenceDetail.data}
              error={dailyBAPresenceDetail.error}
              isLoading={dailyBAPresenceDetail.isLoading}
              maxDate={filters.dateTo}
              minDate={filters.dateFrom}
              onClose={() => setSelectedDailyBAPresence(null)}
              onDateChange={updateDailyBAPresenceDate}
              selectedDate={selectedDailyBAPresenceDate}
            />
          ) : null}

          <section className="dashboard-review-grid">
            <article className="content-card dashboard-review-card">
              <div className="card-heading">
                <div><h2>Attendance exceptions</h2><p>Most recent flagged events awaiting review</p></div>
                <Link className="text-button" to={recordsPath('attendance', { review_status: 'pending' })}>View all <ArrowUpRight size={15} /></Link>
              </div>
              {data?.attendance_attention.length ? (
                <div className="dashboard-review-list">
                  {data.attendance_attention.map((item) => (
                    <Link className="dashboard-review-row" key={item.id} to={attendanceItemPath(item)}>
                      <span className="icon-chip attendance-warning"><AlertTriangle size={17} /></span>
                      <span>
                        <strong>{item.brand_ambassador_name}</strong>
                        <small>{item.campaign_name} · {item.venue_name}, {item.venue_city}</small>
                        <small>{dateTimeFormatter.format(new Date(item.captured_at))} · {readableReason(item.validation_reason)}</small>
                      </span>
                      <StatusPill value="pending" />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state compact dashboard-empty-review">
                  <CheckCircle2 size={30} />
                  <h3>No attendance exceptions</h3>
                  <p>Flagged events in the selected period will appear here.</p>
                </div>
              )}
            </article>

            <article className="content-card dashboard-review-card">
              <div className="card-heading">
                <div><h2>Sales awaiting review</h2><p>Most recent reports without a decision</p></div>
                <Link className="text-button" to={recordsPath('sales', { review_status: 'pending' })}>View all <ArrowUpRight size={15} /></Link>
              </div>
              {data?.sales_attention.length ? (
                <div className="dashboard-review-list">
                  {data.sales_attention.map((item) => (
                    <Link className="dashboard-review-row" key={item.id} to={salesItemPath(item)}>
                      <span className="icon-chip"><ShoppingBag size={17} /></span>
                      <span>
                        <strong>{item.brand_ambassador_name} · {item.total_units} units</strong>
                        <small>{item.campaign_name} · {item.venue_name}, {item.venue_city}</small>
                        <small>{dateTimeFormatter.format(new Date(item.sold_at))}{item.total_value == null ? '' : ` · ${currencyFormatter.format(Number(item.total_value))}`}</small>
                      </span>
                      <StatusPill value="pending" />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state compact dashboard-empty-review">
                  <BadgeCheck size={30} />
                  <h3>No sales reports waiting</h3>
                  <p>Undecided reports in the selected period will appear here.</p>
                </div>
              )}
            </article>
          </section>

          {summary && summary.campaigns_total === 0 ? (
            <section className="content-card dashboard-no-activity">
              <MapPin size={25} />
              <div><h2>No operational activity in this period</h2><p>Adjust the dates or filters to inspect another part of the authorised workspace.</p></div>
            </section>
          ) : null}
        </>
      </DataPanel>
    </>
  );
}
